import React from "react";
import { User } from "lucide-react";

/**
 * Shared building blocks for the /team/* sub-pages.
 * Keeps every department page visually consistent with the GGE
 * dark-first / gold-accent aesthetic used across the rest of the site.
 */

export function DeptHero({ kicker, title, sub, Icon }) {
  return (
    <section
      style={{
        maxWidth: 1180,
        margin: "0 auto",
        padding:
          "clamp(48px, 7vw, 84px) clamp(20px, 5vw, 60px) clamp(28px, 4vw, 40px)",
        textAlign: "center",
      }}
    >
      <span
        className="gg-eyebrow"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 16px",
          borderRadius: 999,
          border: "1px solid var(--line-strong)",
          background: "var(--glass-bg)",
        }}
      >
        {Icon && <Icon size={13} strokeWidth={1.8} />} {kicker}
      </span>
      <h1
        className="gg-h1"
        style={{ marginTop: 22, maxWidth: 860, marginLeft: "auto", marginRight: "auto" }}
      >
        {title}
      </h1>
      {sub && (
        <p
          style={{
            color: "var(--text-dim)",
            fontSize: 17,
            lineHeight: 1.7,
            marginTop: 18,
            maxWidth: 720,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {sub}
        </p>
      )}
    </section>
  );
}

export function SectionHeader({ Icon, title }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        marginBottom: 28,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 14px",
          borderRadius: 999,
          background: "rgba(244,169,31,0.10)",
          border: "1px solid rgba(244,169,31,0.28)",
          color: "var(--gold-soft)",
        }}
      >
        {Icon && <Icon size={16} strokeWidth={1.8} />}
        <span
          style={{
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: 18,
            color: "var(--paper)",
          }}
        >
          {title}
        </span>
      </div>
      <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
    </div>
  );
}

/**
 * Silhouette avatar — matches the placeholder aesthetic used in the
 * original /team hub prior to this import (soft radial gradient + user
 * glyph). Rendered for members without a supplied photo.
 */
export function SilhouetteAvatar({ size = 68 }) {
  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background:
          "radial-gradient(120% 120% at 30% 25%, rgba(244,169,31,0.28), rgba(31,170,89,0.12) 55%, rgba(255,255,255,0.04) 100%)",
        border: "1px solid var(--line)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--text-mute)",
        flexShrink: 0,
      }}
    >
      <User size={Math.round(size * 0.44)} strokeWidth={1.2} />
    </div>
  );
}

/**
 * A compact photo / silhouette container used by the department cards.
 * Keeps aspect + object-fit consistent regardless of source dimensions.
 */
export function MemberPhoto({ src, alt, size = 88 }) {
  if (!src) return <SilhouetteAvatar size={size} />;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        objectFit: "cover",
        border: "1px solid var(--line-strong)",
        flexShrink: 0,
        background: "var(--glass-bg)",
      }}
    />
  );
}

/**
 * A generic "person tile" — used across every department page.
 * `variant="compact"` = single row (photo + name + role); no description.
 * `variant="full"` = photo + name + role + description + optional focus chips.
 */
export function PersonCard({ member, variant = "full", Icon }) {
  const compact = variant === "compact";
  return (
    <div
      className="gg-glass"
      style={{
        padding: compact ? 16 : 22,
        display: "flex",
        gap: 16,
        alignItems: compact ? "center" : "flex-start",
        transition: "border-color 220ms, transform 220ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--line-strong)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--line)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
      data-testid={`member-${member.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <MemberPhoto
        src={member.image}
        alt={member.name}
        size={compact ? 56 : 76}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: compact ? 15.5 : 17,
            color: "var(--paper)",
            lineHeight: 1.25,
          }}
        >
          {member.name}
        </div>
        <div
          style={{
            color: "var(--gold-soft)",
            fontSize: 12.5,
            letterSpacing: "0.06em",
            marginTop: 4,
          }}
        >
          {member.role}
        </div>

        {!compact && member.description && (
          <p
            style={{
              color: "var(--text-dim)",
              fontSize: 14,
              lineHeight: 1.6,
              marginTop: 10,
            }}
          >
            {member.description}
          </p>
        )}

        {!compact && Array.isArray(member.focus) && member.focus.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginTop: 12,
            }}
          >
            {member.focus.map((skill) => (
              <span
                key={skill}
                style={{
                  fontSize: 11,
                  padding: "4px 9px",
                  borderRadius: 999,
                  border: "1px solid var(--line)",
                  color: "var(--text-cloud)",
                  background: "var(--glass-bg)",
                  letterSpacing: "0.02em",
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        )}

        {!compact && member.responsibility && (
          <p
            style={{
              color: "var(--text-dim)",
              fontSize: 14,
              lineHeight: 1.6,
              marginTop: 10,
            }}
          >
            {member.responsibility}
          </p>
        )}

        {member.quote && !compact && (
          <p
            style={{
              color: "var(--text-cloud)",
              fontSize: 13.5,
              lineHeight: 1.6,
              marginTop: 12,
              paddingLeft: 12,
              borderLeft: "2px solid rgba(244,169,31,0.5)",
              fontStyle: "italic",
            }}
          >
            &ldquo;{member.quote}&rdquo;
          </p>
        )}
      </div>
    </div>
  );
}

export function DeptSection({ Icon, title, description, children }) {
  return (
    <section
      style={{
        maxWidth: 1180,
        margin: "0 auto",
        padding: "clamp(24px, 4vw, 40px) clamp(20px, 5vw, 60px)",
      }}
    >
      <SectionHeader Icon={Icon} title={title} />
      {description && (
        <p
          style={{
            color: "var(--text-dim)",
            fontSize: 15,
            lineHeight: 1.7,
            maxWidth: 780,
            marginBottom: 24,
          }}
        >
          {description}
        </p>
      )}
      {children}
    </section>
  );
}

export function CardGrid({ children, minCol = 320 }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${minCol}px, 1fr))`,
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}
